import { stateBase } from '@/helpers/store';
import { StateBase } from '@/types/store';
import { AxiosError } from 'axios';
import { create } from 'zustand';
import { showToast } from '@/stores/app';
import { ToastType } from '@/types/toast';
import { persist, createJSONStorage } from 'zustand/middleware';
import { ICharacterImageData } from '@/models/image';
import imageApi from '@/api/image';
import LocalStorageUtils from '@/utils/storage';

export interface ImageState extends StateBase {
  loading: boolean;
  updateRequired: boolean;
  characterImages: { [character_id: number]: ICharacterImageData };
  getUnlockedImagesSummary: () => Promise<boolean>;
  setUpdateRequired: (value: boolean) => void;
  deleteImages: (characterId: number, imageIds: number[]) => Promise<void>;
}

// TODO: Remove this after testing, at some point, 
// we should clean up the previous storage key: 'image-store'
export const imageStorePersistKey = 'image-store-v2';

export const useImageStore = create<ImageState>()(
  persist(
    (set, get) => ({
      ...stateBase,
      loading: false,
      updateRequired: false,
      characterImages: {},

      getUnlockedImagesSummary: async () => {
        // Skip fetch if data is fresh
        if (Object.keys(get().characterImages).length > 0 && !get().updateRequired) {
          return true;
        }

        set({ loading: true });
        try {
          const summaryData = await imageApi.getUnlockedImagesSummary();
          const newSummaryData: { [id: number]: ICharacterImageData } = {};

          // Only update counts that changed
          const currentData = get().characterImages;
          for (const charIdStr in summaryData) {
            const charId = Number(charIdStr);
            const newCount = summaryData[charId]?.image_count || 0;
            const currentCount = currentData[charId]?.image_count || 0;
            
            if (newCount !== currentCount) {
              newSummaryData[charId] = {
                image_count: newCount,
                images: summaryData[charId]?.images || [],
              };
            }
          }

          set(state => ({
            characterImages: {
              ...state.characterImages,
              ...newSummaryData
            },
            updateRequired: false,
            loading: false
          }));
          return true;
        } catch (error) {
          const axiosError = error as AxiosError<any, any>;
          console.error('Failed to get unlocked images summary:', error);
          showToast(`Can't get image summary: ${axiosError.response?.data?.detail || axiosError.message}`, ToastType.ERROR);
          set({ loading: false, updateRequired: false });
          return false;
        }
      },

      setUpdateRequired: (value: boolean) => {
        set({ updateRequired: value });
      },

      deleteImages: async (characterId: number, imageIds: number[]) => {
        set(state => {
          const currentData = state.characterImages[characterId];
          if (!currentData) return state;

          const newCount = Math.max(0, (currentData.image_count || 0) - imageIds.length);
          
          return {
            characterImages: {
              ...state.characterImages,
              [characterId]: { ...currentData, image_count: newCount }
            },
            updateRequired: false
          };
        });
      },
    }),
    {
      name: imageStorePersistKey,
      storage: createJSONStorage(() =>  LocalStorageUtils.zustandStorageAdapter),
      partialize: state => ({
        characterImages: state.characterImages,
        updateRequired: state.updateRequired
      })
    }
  )
);
